import { type Breadcrumb, useBreadcrumb } from "components/Breadcrumb";
import { EmailConnectionRequiredBanner } from "components/EmailConnectionRequiredBanner";
import { ReplyComposeModal } from "components/ReplyComposeModal";
import { Loading } from "components/ui/Loading/Loading";
import type { ReplyComposeSeed } from "hooks/use-email-compose";
import { useActiveGmailConnection } from "hooks/use-email-integrations-api";
import {
  type ReplyEmailItem,
  type ReplyResponseStatus,
  useReplies,
  useReplyBatch,
  useReplyStats,
  useUpdateReplyStatus,
} from "hooks/use-email-replies-api";
import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { toastError } from "utils/toast-utils";

import { BatchActionBar } from "./components/BatchActionBar";
import { CrmLinkDialog } from "./components/CrmLinkDialog";
import { ReplyDashboardHeader } from "./components/ReplyDashboardHeader";
import {
  ReplyFilterBar,
  type ReplyLabel,
  type ReplyPeriod,
} from "./components/ReplyFilterBar";
import { ReplyListItem } from "./components/ReplyListItem";
import { ReplyThreadPane } from "./components/ReplyThreadPane";
import {
  nextLabel,
  useReplyKeyboardShortcuts,
} from "./hooks/use-reply-keyboard-shortcuts";
import { buildReplySeed } from "./lib/build-reply-seed";
import { groupRepliesByThread } from "./lib/group-replies-by-thread";

const PERIOD_HOURS: Record<ReplyPeriod, number | null> = {
  all: null,
  "24h": 24,
  "7d": 24 * 7,
  "30d": 24 * 30,
};

const BREAD_CRUMBS: Breadcrumb[] = [
  { label: "ホーム", to: "/" },
  { label: "メール" },
  { label: "返信管理" },
];

/**
 * 返信管理ページ. 2 カラム (左: 一覧 / 右: スレッド + アクション) 構成.
 * @return {React.ReactElement} 返信管理画面
 */
export const EmailRepliesPage: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const { setBreadcrumbItems } = useBreadcrumb();

  useEffect(() => {
    setBreadcrumbItems(BREAD_CRUMBS);
  }, [setBreadcrumbItems]);

  const status =
    (params.get("status") as ReplyResponseStatus | "all" | null) ?? "all";
  const label = (params.get("label") as ReplyLabel | null) ?? "all";
  const period = (params.get("period") as ReplyPeriod | null) ?? "all";
  const query = params.get("q") ?? "";
  const seriesId = params.get("seriesId");
  const selectedReplyId = params.get("id");

  const setParam = useCallback(
    (key: string, value: string | null) => {
      setParams(prev => {
        const current = prev.get(key);
        const shouldDelete = value === null || value === "" || value === "all";
        if (shouldDelete && current === null) return prev;
        if (!shouldDelete && current === value) return prev;
        const next = new URLSearchParams(prev);
        if (shouldDelete) {
          next.delete(key);
        } else {
          next.set(key, value);
        }
        return next;
      });
    },
    [setParams],
  );

  const setStatus = useCallback(
    (next: ReplyResponseStatus | "all") =>
      setParam("status", next === "all" ? null : next),
    [setParam],
  );
  const setLabel = useCallback(
    (next: ReplyLabel) => setParam("label", next === "all" ? null : next),
    [setParam],
  );
  const setPeriod = useCallback(
    (next: ReplyPeriod) => setParam("period", next === "all" ? null : next),
    [setParam],
  );
  const setQuery = useCallback(
    (next: string) => setParam("q", next === "" ? null : next),
    [setParam],
  );
  const setSeriesId = useCallback(
    (next: string | null) => setParam("seriesId", next),
    [setParam],
  );
  const setSelectedId = useCallback(
    (id: string | null) => setParam("id", id),
    [setParam],
  );

  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [replyModalSeed, setReplyModalSeed] = useState<ReplyComposeSeed | null>(
    null,
  );
  const [crmDialogReply, setCrmDialogReply] = useState<ReplyEmailItem | null>(
    null,
  );

  const focusSearchInput = useCallback(() => {
    const el = document.querySelector<HTMLInputElement>(
      "[data-reply-search-input]",
    );
    el?.focus();
    el?.select();
  }, []);

  const { stats, refetch: refetchStats } = useReplyStats();
  const { hasActiveConnection, loading: connectionLoading } =
    useActiveGmailConnection();
  const filters = useMemo(
    () => ({
      status: status === "all" ? null : status,
      seriesId,
      page: 1,
      limit: 100,
    }),
    [status, seriesId],
  );
  const {
    items: rawItems,
    loading,
    error: repliesError,
    refetch,
  } = useReplies(filters);
  const { updateStatus } = useUpdateReplyStatus();
  const { batch } = useReplyBatch();

  // client-side フィルター: ラベル / 期間 / 全文検索
  const items = useMemo(() => {
    const periodHours = PERIOD_HOURS[period];
    const since = periodHours ? Date.now() - periodHours * 3600 * 1000 : null;
    const lowered = query.trim().toLowerCase();
    return rawItems.filter(r => {
      if (label !== "all" && r.parentType !== label) return false;
      if (since !== null) {
        const t = new Date(r.receivedAt).getTime();
        if (Number.isFinite(t) && t < since) return false;
      }
      if (lowered) {
        const haystack = [r.fromEmail, r.fromName, r.subject, r.bodyText]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(lowered)) return false;
      }
      return true;
    });
  }, [rawItems, label, period, query]);

  // スレッド単位グルーピング: 1 thread = 1 行 (Gmail 風)
  const threadGroups = useMemo(() => groupRepliesByThread(items), [items]);

  const groupLatestReplies = useMemo<ReplyEmailItem[]>(
    () => threadGroups.map(g => g.latest),
    [threadGroups],
  );

  const selectedReply = useMemo<ReplyEmailItem | null>(() => {
    if (!selectedReplyId) return null;
    const group = threadGroups.find(g =>
      g.replies.some(r => r.id === selectedReplyId),
    );
    return group?.latest ?? null;
  }, [threadGroups, selectedReplyId]);

  // items が変わって selectedReplyId が一覧に居なくなったら選択解除
  useEffect(() => {
    if (
      selectedReplyId &&
      !threadGroups.some(g => g.replies.some(r => r.id === selectedReplyId))
    ) {
      setSelectedId(null);
    }
  }, [threadGroups, selectedReplyId, setSelectedId]);

  const toggleChecked = useCallback((id: string) => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setCheckedIds(new Set()), []);

  /**
   * 1 件のステータスを変更する
   * @param {string} replyId Reply ID
   * @param {ReplyResponseStatus} statusNext 新しいステータス
   * @return {Promise<void>}
   */
  const handleSetStatus = useCallback(
    async (replyId: string, statusNext: ReplyResponseStatus) => {
      try {
        await updateStatus(replyId, statusNext);
        toast.success("ステータスを更新しました");
        await Promise.all([refetch(), refetchStats()]);
      } catch {
        toastError("ステータスの更新に失敗しました");
      }
    },
    [updateStatus, refetch, refetchStats],
  );

  /**
   * 選択した複数返信を一括更新する
   * @param {"mark_completed"|"mark_in_progress"|"mark_unresponded"} action アクション
   * @return {Promise<void>}
   */
  const handleBatch = useCallback(
    async (
      action: "mark_completed" | "mark_in_progress" | "mark_unresponded",
    ) => {
      if (checkedIds.size === 0) return;
      try {
        const updated = await batch(Array.from(checkedIds), action);
        toast.success(`${updated}件を更新しました`);
        setCheckedIds(new Set());
        await Promise.all([refetch(), refetchStats()]);
      } catch {
        toastError("一括更新に失敗しました");
      }
    },
    [batch, checkedIds, refetch, refetchStats],
  );

  /**
   * フル機能の返信モーダルを開く
   * @return {void}
   */
  const handleOpenReplyModal = useCallback(() => {
    if (!selectedReply || !hasActiveConnection) return;
    setReplyModalSeed(buildReplySeed(selectedReply));
  }, [selectedReply, hasActiveConnection]);

  /**
   * インライン / フルどちらかで返信送信が成功したときの追従処理
   * @return {void}
   */
  const handleReplySent = useCallback(() => {
    refetch();
    refetchStats();
  }, [refetch, refetchStats]);

  useReplyKeyboardShortcuts({
    items: groupLatestReplies,
    selectedId: selectedReplyId,
    setSelectedId,
    toggleChecked,
    onSetStatusCurrent: statusNext => {
      if (selectedReplyId) handleSetStatus(selectedReplyId, statusNext);
    },
    onStartReply: handleOpenReplyModal,
    onMoveLabel: dir => setLabel(nextLabel(label, dir)),
    onFocusSearch: focusSearchInput,
  });

  const totalCount = threadGroups.length;
  const allChecked = checkedIds.size > 0 && checkedIds.size === totalCount;

  return (
    <div className="flex flex-col h-[calc(100vh-60px)] p-3 gap-3">
      <ReplyDashboardHeader stats={stats} />
      {!connectionLoading && !hasActiveConnection && (
        <EmailConnectionRequiredBanner />
      )}
      {repliesError && (
        <div
          className="bg-rose-50 border border-rose-200 text-rose-700 rounded-lg px-4 py-3 text-sm flex items-start justify-between gap-3"
          role="alert"
        >
          <div>
            <div className="font-semibold">
              リプライ一覧の取得に失敗しました
            </div>
            <div className="text-xs text-rose-600 mt-0.5">
              {repliesError.message ??
                "サーバーでエラーが発生しました。 時間をおいて再試行してください。"}
            </div>
          </div>
          <button
            type="button"
            onClick={() => refetch()}
            className="text-xs font-semibold text-rose-700 hover:text-rose-900 hover:underline shrink-0"
          >
            再読み込み
          </button>
        </div>
      )}
      <ReplyFilterBar
        status={status}
        label={label}
        period={period}
        query={query}
        seriesId={seriesId}
        stats={stats}
        onStatusChange={setStatus}
        onLabelChange={setLabel}
        onPeriodChange={setPeriod}
        onQueryChange={setQuery}
        onSeriesChange={setSeriesId}
      />

      <div className="flex-1 min-h-0 flex gap-3 overflow-hidden">
        <div className="flex flex-col min-h-0 bg-white rounded-lg border border-slate-200 overflow-hidden basis-[38%] min-w-[320px] max-w-[480px]">
          <BatchActionBar
            checkedCount={checkedIds.size}
            totalCount={totalCount}
            onToggleAll={() => {
              if (allChecked) {
                setCheckedIds(new Set());
              } else {
                setCheckedIds(new Set(threadGroups.map(g => g.latest.id)));
              }
            }}
            onClearSelection={clearSelection}
            onBatch={handleBatch}
          />
          {loading ? (
            <div className="flex justify-center py-8">
              <Loading />
            </div>
          ) : threadGroups.length === 0 ? (
            <div className="flex-1 flex items-center justify-center text-sm text-base-content/60">
              該当する返信はありません
            </div>
          ) : (
            <ul className="flex-1 overflow-auto">
              {threadGroups.map(group => (
                <ReplyListItem
                  key={group.key}
                  reply={group.latest}
                  threadCount={group.count}
                  originLabel={group.originLabel}
                  aggregateStatus={group.aggregateStatus}
                  selected={group.replies.some(r => r.id === selectedReplyId)}
                  checked={checkedIds.has(group.latest.id)}
                  onSelect={setSelectedId}
                  onToggleChecked={toggleChecked}
                  onChangeStatus={handleSetStatus}
                />
              ))}
            </ul>
          )}
        </div>

        <div className="flex-1 min-w-0 min-h-0 flex">
          <ReplyThreadPane
            reply={selectedReply}
            canCompose={hasActiveConnection}
            onSetStatus={handleSetStatus}
            onStartReply={handleOpenReplyModal}
            onOpenCrmLink={() => setCrmDialogReply(selectedReply)}
            onReplySent={handleReplySent}
          />
        </div>
      </div>

      <ReplyComposeModal
        isOpen={replyModalSeed !== null}
        seed={replyModalSeed}
        onClose={() => setReplyModalSeed(null)}
        onSent={handleReplySent}
      />
      <CrmLinkDialog
        isOpen={crmDialogReply !== null}
        reply={crmDialogReply}
        onClose={() => setCrmDialogReply(null)}
        onLinked={() => {
          refetch();
          setCrmDialogReply(null);
        }}
      />
    </div>
  );
};
